/**
 * DP / Folha — Consignado
 * Cadastro permanente de empréstimos consignados por colaborador.
 * O desconto é lançado automaticamente na folha, férias ou rescisão do
 * colaborador — o que existir primeiro naquele mês.
 *
 * O filtro Mês/Ano consulta o histórico de arquivos importados: mostra quem
 * estava no arquivo daquele mês, mesmo que o empréstimo já tenha sido
 * quitado/encerrado depois (histórico não desaparece com o tempo).
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Loader2, Upload, FileDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const fmtBRL = (v) => v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const ConsignadoTab = () => {
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [baixandoPdfId, setBaixandoPdfId] = useState(null);
  const agora = new Date();
  const [mes, setMes] = useState(agora.getMonth() + 1);
  const [ano, setAno] = useState(agora.getFullYear());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/consignados/historico`,
        { ...authHeader(), params: { mes, ano } });
      setItens(r.data || []);
    } catch (e) { toast.error('Erro ao listar consignados'); }
    finally { setLoading(false); }
  }, [mes, ano]);
  useEffect(() => { load(); }, [load]);

  const baixarPDF = async () => {
    setBaixandoPdf(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/consignados/pdf`,
        { ...authHeader(), params: { mes, ano }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Erro ao gerar PDF');
    } finally {
      setBaixandoPdf(false);
    }
  };

  const baixarPDFIndividual = async (employeeId) => {
    setBaixandoPdfId(employeeId);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/consignados/${employeeId}/pdf`,
        { ...authHeader(), params: { mes, ano }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Erro ao gerar PDF do colaborador');
    } finally {
      setBaixandoPdfId(null);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await axios.post(`${API}/api/calculos-dp/consignados/importar`, formData, authHeader());
      const { criados, atualizados, desativados, nao_encontrados } = r.data;
      let msg = `${criados} colaborador(es) novo(s), ${atualizados} atualizado(s)`;
      if (desativados) msg += `, ${desativados} encerrado(s) (não vieram mais no arquivo)`;
      toast.success(msg, { duration: 6000 });
      if (nao_encontrados?.length) {
        toast.error(
          `${nao_encontrados.length} colaborador(es) não cadastrado(s): ${nao_encontrados.map((n) => `${n.nome} (${n.cpf})`).join(', ')}. Cadastre-os e importe novamente.`,
          { duration: 12000 }
        );
        console.warn('Consignado - colaboradores não encontrados:', nao_encontrados);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const remove = async (consignadoId) => {
    if (!window.confirm('Remover este empréstimo consignado? Ele deixará de ser descontado automaticamente.')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/consignados/${consignadoId}`, authHeader());
      toast.success('Removido'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao remover'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="text-sm text-slate-600">
          <p className="font-medium text-slate-800">{itens.length} colaborador(es) no arquivo de {MESES[mes - 1]}/{ano}</p>
          <p className="text-xs">O desconto é lançado automaticamente na folha, férias ou rescisão do colaborador — o que existir primeiro naquele mês. Reimporte o arquivo do banco todo mês: quem não vier mais é marcado como encerrado.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={baixarPDF} disabled={baixandoPdf} data-testid="dp-consignado-pdf-btn">
            {baixandoPdf ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
            Baixar PDF
          </Button>
          <label className="inline-flex items-center gap-2 text-sm font-medium border rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50 bg-white"
            data-testid="dp-consignado-import-label">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Importar Consignado (JSON/CSV)
            <input type="file" accept=".json,.csv" className="hidden" disabled={importing} onChange={handleImport} data-testid="dp-consignado-import-input" />
          </label>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <Label className="text-xs">Mês</Label>
          <Select value={String(mes)} onValueChange={(v) => setMes(parseInt(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>{MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Ano</Label>
          <Input className="w-24" value={ano} onChange={(e) => setAno(parseInt(e.target.value.replace(/\D/g, '')) || ano)} inputMode="numeric" />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-left p-3">Banco</th>
              <th className="text-left p-3">Contrato</th>
              <th className="text-right p-3">Parcela</th>
              <th className="text-right p-3">Parcelas</th>
              <th className="text-left p-3">Status</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-center"><Loader2 className="h-4 w-4 inline animate-spin" /></td></tr>
            ) : itens.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-center text-slate-500">Nenhum consignado no arquivo importado para {MESES[mes - 1]}/{ano}.</td></tr>
            ) : itens.map((c) => (
              <tr key={c.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-medium">{c.employee_nome}</td>
                <td className="p-3 text-xs">{c.banco || '—'}</td>
                <td className="p-3 text-xs font-mono">{c.contrato || '—'}</td>
                <td className="p-3 text-right">{fmtBRL(c.valor_parcela)}</td>
                <td className="p-3 text-right text-xs">
                  {c.parcela_atual && c.total_parcelas ? `${c.parcela_atual}/${c.total_parcelas}` : '—'}
                </td>
                <td className="p-3 text-xs">
                  {c.ativo_atualmente
                    ? <span className="text-emerald-700 font-medium">Ativo</span>
                    : <span className="text-slate-400">Encerrado</span>}
                </td>
                <td className="p-3 text-right">
                  {c.ativo_atualmente && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => baixarPDFIndividual(c.employee_id)}
                        disabled={baixandoPdfId === c.employee_id} title="Baixar PDF do colaborador"
                        data-testid={`dp-consignado-pdf-${c.id}`}>
                        {baixandoPdfId === c.employee_id
                          ? <Loader2 className="h-4 w-4 animate-spin" />
                          : <FileDown className="h-4 w-4 text-indigo-600" />}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c.consignado_id)} title="Remover">
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConsignadoTab;
